import { Project, ProjectFilterState } from "../../../types/global/types";

export const actionTypes = {
  FETCH_PROJECTS: "FETCH_PROJECTS",
  FETCH_PROJECTS_SUCCESS: "FETCH_PROJECTS_SUCCESS",
  FETCH_PROJECTS_ERROR: "FETCH_PROJECTS_ERROR",
  CREATE_PROJECT: "CREATE_PROJECT",
  CREATE_PROJECT_SUCCESS: "CREATE_PROJECT_SUCCESS",
  UPDATE_PROJECT: "UPDATE_PROJECT",
  UPDATE_PROJECT_SUCCESS: "UPDATE_PROJECT_SUCCESS",
  CLOSE_PROJECT: "CLOSE_PROJECT",
  CLOSE_PROJECT_SUCCESS: "CLOSE_PROJECT_SUCCESS",
  OPEN_PROJECT: "OPEN_PROJECT",
  OPEN_PROJECT_SUCCESS: "OPEN_PROJECT_SUCCESS",
  DELETE_PROJECT: "DELETE_PROJECT",
  DELETE_PROJECT_SUCCESS: "DELETE_PROJECT_SUCCESS",
  CONFIRM_DELETE_PROJECT: "CONFIRM_DELETE_PROJECT",
  SET_CURRENT_PROJECT: "SET_CURRENT_PROJECT",
  SET_CURRENT_PROJECT_SUCCESS: "SET_CURRENT_PROJECT_SUCCESS",
  SET_PROJECT_FILTER: "SET_PROJECT_FILTER",
  SET_PROJECT_FILTER_SUCCESS: "SET_PROJECT_FILTER_SUCCESS",
  BULK_MOVE_ROUTES: "BULK_MOVE_ROUTES",
  BULK_MOVE_ROUTES_SUCCESS: "BULK_MOVE_ROUTES_SUCCESS",
  SET_PROJECTS_ERROR: "SET_PROJECTS_ERROR",
} as const;

interface InitialState {
  projects: Project[];
  currentProjectId: number | null;
  filterState: ProjectFilterState;
  loading: boolean;
  error: string | null;
}

const initialState: InitialState = {
  projects: [],
  currentProjectId: null,
  filterState: {},
  loading: false,
  error: null,
};

export const projectsReducer = (state = initialState, action: any): InitialState => {
  switch (action.type) {
    case actionTypes.FETCH_PROJECTS:
      return { ...state, loading: true, error: null };
    case actionTypes.FETCH_PROJECTS_SUCCESS:
      return { ...state, loading: false, projects: action.payload };
    case actionTypes.FETCH_PROJECTS_ERROR:
      return { ...state, loading: false, error: action.payload };
    case actionTypes.CREATE_PROJECT_SUCCESS:
      return { ...state, projects: [...state.projects, action.payload] };
    case actionTypes.UPDATE_PROJECT_SUCCESS:
      return {
        ...state,
        projects: state.projects.map((p) => (p.id === action.payload.id ? action.payload : p)),
      };
    case actionTypes.CLOSE_PROJECT_SUCCESS:
      return {
        ...state,
        projects: state.projects.map((p) =>
          p.id === action.payload ? { ...p, status: 'closed' as const } : p
        ),
        // If this project was current, clear it
        currentProjectId: state.currentProjectId === action.payload ? null : state.currentProjectId,
      };
    case actionTypes.OPEN_PROJECT_SUCCESS:
      return {
        ...state,
        projects: state.projects.map((p) =>
          p.id === action.payload ? { ...p, status: 'open' as const } : p
        ),
      };
    case actionTypes.DELETE_PROJECT_SUCCESS:
      return {
        ...state,
        projects: state.projects.filter((p) => p.id !== action.payload),
        currentProjectId: state.currentProjectId === action.payload ? null : state.currentProjectId,
      };
    case actionTypes.SET_CURRENT_PROJECT_SUCCESS:
      return { ...state, currentProjectId: action.payload };
    case actionTypes.SET_PROJECT_FILTER_SUCCESS:
      return { ...state, filterState: action.payload };
    case actionTypes.SET_PROJECTS_ERROR:
      return { ...state, error: action.payload, loading: false };
    default:
      return state;
  }
};

export const projectsActions = {
  fetchProjects: () => ({ type: actionTypes.FETCH_PROJECTS }),
  fetchProjectsSuccess: (projects: Project[]) => ({
    type: actionTypes.FETCH_PROJECTS_SUCCESS,
    payload: projects,
  }),
  fetchProjectsError: (error: string) => ({
    type: actionTypes.FETCH_PROJECTS_ERROR,
    payload: error,
  }),
  createProject: (data: { name: string; default_depot_address_id?: number | null }) => ({
    type: actionTypes.CREATE_PROJECT,
    payload: data,
  }),
  createProjectSuccess: (project: Project) => ({
    type: actionTypes.CREATE_PROJECT_SUCCESS,
    payload: project,
  }),
  updateProject: (id: number, data: { name?: string; color?: string; default_depot_address_id?: number | null }) => ({
    type: actionTypes.UPDATE_PROJECT,
    id,
    payload: data,
  }),
  updateProjectSuccess: (project: Project) => ({
    type: actionTypes.UPDATE_PROJECT_SUCCESS,
    payload: project,
  }),
  closeProject: (id: number) => ({ type: actionTypes.CLOSE_PROJECT, payload: id }),
  closeProjectSuccess: (id: number) => ({ type: actionTypes.CLOSE_PROJECT_SUCCESS, payload: id }),
  openProject: (id: number) => ({ type: actionTypes.OPEN_PROJECT, payload: id }),
  openProjectSuccess: (id: number) => ({ type: actionTypes.OPEN_PROJECT_SUCCESS, payload: id }),
  deleteProject: (id: number) => ({ type: actionTypes.DELETE_PROJECT, payload: id }),
  deleteProjectSuccess: (id: number) => ({ type: actionTypes.DELETE_PROJECT_SUCCESS, payload: id }),
  confirmDeleteProject: (id: number, confirmName: string) => ({
    type: actionTypes.CONFIRM_DELETE_PROJECT,
    id,
    confirmName,
  }),
  setCurrentProject: (projectId: number | null) => ({
    type: actionTypes.SET_CURRENT_PROJECT,
    payload: projectId,
  }),
  setCurrentProjectSuccess: (projectId: number | null) => ({
    type: actionTypes.SET_CURRENT_PROJECT_SUCCESS,
    payload: projectId,
  }),
  setProjectFilter: (filterState: ProjectFilterState) => ({
    type: actionTypes.SET_PROJECT_FILTER,
    payload: filterState,
  }),
  setProjectFilterSuccess: (filterState: ProjectFilterState) => ({
    type: actionTypes.SET_PROJECT_FILTER_SUCCESS,
    payload: filterState,
  }),
  bulkMoveRoutes: (routeIds: number[], targetProjectId: number) => ({
    type: actionTypes.BULK_MOVE_ROUTES,
    routeIds,
    targetProjectId,
  }),
  setProjectsError: (error: string) => ({ type: actionTypes.SET_PROJECTS_ERROR, payload: error }),
};
